// Visual model of the Postgres schema (packages/api/prisma/schema.prisma),
// hand-transcribed for the ERD on the /docs "Data Model" section. Kept in sync
// manually; erd-model.test.ts guards structural integrity (every FK points at a
// real table/column, every table maps to a real docs section).

import type { SectionId } from '../nav';

export type ErdGroup = 'core' | 'loop' | 'prototype' | 'config' | 'analytics';

interface ErdColumn {
  name: string;
  type: string;
  pk?: boolean;
  /** Target table name when this column is a foreign key. */
  fk?: string;
}

export interface ErdTable {
  name: string;
  group: ErdGroup;
  /** Docs section a click on this table jumps to. */
  section: SectionId;
  columns: ErdColumn[];
  position: { x: number; y: number };
}

export interface ErdRelation {
  from: string; // parent table
  to: string; // child table (holds the FK)
  fromCol: string;
  toCol: string;
  cardinality: '1:1' | '1:N';
}

const COL = {
  id: { name: 'id', type: 'uuid', pk: true } satisfies ErdColumn,
  createdAt: { name: 'createdAt', type: 'DateTime' } satisfies ErdColumn,
  updatedAt: { name: 'updatedAt', type: 'DateTime' } satisfies ErdColumn,
};

export const ERD_TABLES: ErdTable[] = [
  // ── Multi-tenancy root ────────────────────────────────────────────────────
  {
    name: 'Org',
    group: 'config',
    section: 'security',
    position: { x: -400, y: 240 },
    columns: [
      COL.id,
      { name: 'name', type: 'String' },
      { name: 'slug', type: 'String' },
      COL.createdAt,
      COL.updatedAt,
    ],
  },
  {
    name: 'Membership',
    group: 'config',
    section: 'security',
    position: { x: 1440, y: -200 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'userId', type: 'uuid', fk: 'User' },
      { name: 'role', type: 'String' },
      COL.createdAt,
    ],
  },
  // ── Core spine ────────────────────────────────────────────────────────────
  {
    name: 'Project',
    group: 'core',
    section: 'projects',
    position: { x: 0, y: 240 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'name', type: 'String' },
      { name: 'repoFullName', type: 'String' },
      { name: 'repoUrl', type: 'String' },
      { name: 'defaultBranch', type: 'String' },
      { name: 'issueLabelFilter', type: 'String?' },
      { name: 'kind', type: 'ProjectKind' },
      { name: 'description', type: 'String' },
      COL.createdAt,
      COL.updatedAt,
    ],
  },
  {
    name: 'Issue',
    group: 'core',
    section: 'issues',
    position: { x: 340, y: 40 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'projectId', type: 'uuid', fk: 'Project' },
      { name: 'title', type: 'String' },
      { name: 'body', type: 'String' },
      { name: 'state', type: 'IssueState' },
      { name: 'resolution', type: 'IssueResolution?' },
      { name: 'pipeline', type: 'String[]' },
      { name: 'githubNumber', type: 'Int?' },
      { name: 'source', type: 'IssueSource' },
      { name: 'tags', type: 'String[]' },
      { name: 'externalRef', type: 'String?' },
      { name: 'loopId', type: 'uuid?', fk: 'Loop' },
      { name: 'createdByUserId', type: 'uuid?', fk: 'User' },
      COL.createdAt,
      COL.updatedAt,
    ],
  },
  {
    name: 'IssueEvent',
    group: 'core',
    section: 'issues',
    position: { x: 700, y: 0 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'issueId', type: 'uuid', fk: 'Issue' },
      { name: 'runId', type: 'uuid?' },
      { name: 'type', type: 'String' },
      { name: 'actor', type: 'String?' },
      { name: 'data', type: 'Json?' },
      COL.createdAt,
    ],
  },
  {
    name: 'Run',
    group: 'core',
    section: 'run-lifecycle',
    position: { x: 700, y: 300 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'issueId', type: 'uuid', fk: 'Issue' },
      { name: 'projectId', type: 'uuid', fk: 'Project' },
      { name: 'status', type: 'RunStatus' },
      { name: 'pipeline', type: 'String[]' },
      { name: 'lane', type: 'RunLane' },
      { name: 'branch', type: 'String?' },
      { name: 'commitSha', type: 'String?' },
      { name: 'pullRequestUrl', type: 'String?' },
      { name: 'pullRequestState', type: 'PullRequestState' },
      { name: 'totalCostUsd', type: 'Float?' },
      { name: 'diff', type: 'String?' },
      { name: 'parentRunId', type: 'uuid?' },
      { name: 'riskLevel', type: 'RiskLevel?' },
      { name: 'autoMergeStatus', type: 'String?' },
      { name: 'scorecard', type: 'Json?' },
      { name: 'peakMemoryMb', type: 'Float?' },
      { name: 'createdByUserId', type: 'uuid?' },
      COL.createdAt,
    ],
  },
  {
    name: 'Stage',
    group: 'core',
    section: 'run-lifecycle',
    position: { x: 1080, y: 120 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'runId', type: 'uuid', fk: 'Run' },
      { name: 'role', type: 'String' },
      { name: 'order', type: 'Int' },
      { name: 'status', type: 'StageStatus' },
      { name: 'costUsd', type: 'Float?' },
      { name: 'summary', type: 'String?' },
      { name: 'gateOutput', type: 'String?' },
      { name: 'outputTokens', type: 'Int?' },
    ],
  },
  {
    name: 'LogLine',
    group: 'core',
    section: 'runs',
    position: { x: 1080, y: 400 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'runId', type: 'uuid', fk: 'Run' },
      { name: 'seq', type: 'Int' },
      { name: 'stream', type: 'String' },
      { name: 'role', type: 'String?' },
      { name: 'text', type: 'String' },
      { name: 'kind', type: 'String?' },
      { name: 'meta', type: 'Json?' },
    ],
  },
  {
    name: 'StageAttempt',
    group: 'core',
    section: 'run-lifecycle',
    position: { x: 1080, y: 640 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'runId', type: 'uuid', fk: 'Run' },
      { name: 'order', type: 'Int' },
      { name: 'iteration', type: 'Int' },
      { name: 'gatePassed', type: 'Boolean?' },
      { name: 'loopReason', type: 'String?' },
    ],
  },
  // ── Scheduled loops ───────────────────────────────────────────────────────
  {
    name: 'Loop',
    group: 'loop',
    section: 'loops',
    position: { x: 340, y: 520 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'projectId', type: 'uuid', fk: 'Project' },
      { name: 'name', type: 'String' },
      { name: 'description', type: 'String' },
      { name: 'prompt', type: 'String' },
      { name: 'cronExpression', type: 'String' },
      { name: 'enabled', type: 'Boolean' },
      { name: 'pipeline', type: 'String[]' },
      { name: 'mode', type: 'LoopMode' },
      { name: 'skipCount', type: 'Int' },
      { name: 'lastFiredAt', type: 'DateTime?' },
      { name: 'nextRunAt', type: 'DateTime?' },
      { name: 'createdByUserId', type: 'uuid?' },
    ],
  },
  // ── Per-project knowledge ─────────────────────────────────────────────────
  {
    name: 'ProjectMemory',
    group: 'core',
    section: 'projects',
    position: { x: -400, y: 0 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'projectId', type: 'uuid', fk: 'Project' },
      { name: 'content', type: 'String' },
      COL.updatedAt,
    ],
  },
  {
    name: 'ProjectWiki',
    group: 'core',
    section: 'projects',
    position: { x: -400, y: 480 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'projectId', type: 'uuid', fk: 'Project' },
      { name: 'content', type: 'String' },
      COL.updatedAt,
    ],
  },
  // ── Prototype deployments ─────────────────────────────────────────────────
  {
    name: 'PrototypeDeployment',
    group: 'prototype',
    section: 'prototypes',
    position: { x: 340, y: 900 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'projectId', type: 'uuid', fk: 'Project' },
      { name: 'slug', type: 'String' },
      { name: 'liveUrl', type: 'String' },
      { name: 'imageTag', type: 'String' },
      { name: 'deployStatus', type: 'DeployStatus' },
      { name: 'lastDeployedSha', type: 'String?' },
      { name: 'lastDeployedAt', type: 'DateTime?' },
      { name: 'reclaimedMb', type: 'Int' },
      { name: 'fullstack', type: 'Boolean' },
      COL.createdAt,
      COL.updatedAt,
    ],
  },
  {
    name: 'PrototypeSecret',
    group: 'prototype',
    section: 'prototypes',
    position: { x: 720, y: 820 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'deploymentId', type: 'uuid', fk: 'PrototypeDeployment' },
      { name: 'key', type: 'String' },
      { name: 'valueEnc', type: 'String' },
      { name: 'visible', type: 'Boolean' },
      COL.createdAt,
      COL.updatedAt,
    ],
  },
  {
    name: 'PrototypeCustomDomain',
    group: 'prototype',
    section: 'prototypes',
    position: { x: 720, y: 1040 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'deploymentId', type: 'uuid', fk: 'PrototypeDeployment' },
      { name: 'domain', type: 'String' },
      { name: 'status', type: 'CustomDomainStatus' },
      { name: 'lastError', type: 'String?' },
      { name: 'lastCheckedAt', type: 'DateTime?' },
      COL.createdAt,
    ],
  },
  // ── Configuration singletons (no relations) ───────────────────────────────
  {
    name: 'User',
    group: 'config',
    section: 'security',
    position: { x: 1440, y: 0 },
    columns: [
      COL.id,
      { name: 'email', type: 'String' },
      { name: 'passwordHash', type: 'String' },
      { name: 'role', type: 'String' },
      { name: 'totpSecretEnc', type: 'String?' },
      { name: 'totpEnabled', type: 'Boolean' },
      { name: 'recoveryCodesHashed', type: 'Json?' },
      COL.createdAt,
      COL.updatedAt,
    ],
  },
  {
    name: 'Setting',
    group: 'config',
    section: 'settings',
    position: { x: 1440, y: 300 },
    columns: [
      COL.id,
      { name: 'key', type: 'String' },
      { name: 'valueEnc', type: 'String' },
      COL.updatedAt,
    ],
  },
  {
    // Per-workspace BYO secrets (Claude / GitHub / Telegram). RLS-enforced.
    name: 'OrgSetting',
    group: 'config',
    section: 'settings',
    position: { x: 1800, y: 300 },
    columns: [
      COL.id,
      { name: 'orgId', type: 'uuid', fk: 'Org' },
      { name: 'key', type: 'String' },
      { name: 'valueEnc', type: 'String' },
      COL.createdAt,
      COL.updatedAt,
    ],
  },
  // ── Analytics singleton (no relations) ────────────────────────────────────
  {
    name: 'UiEvent',
    group: 'analytics',
    section: 'metrics',
    position: { x: 0, y: 560 },
    columns: [
      COL.id,
      { name: 'type', type: 'String' },
      { name: 'screen', type: 'String' },
      { name: 'element', type: 'String?' },
      { name: 'userId', type: 'uuid?' },
      COL.createdAt,
    ],
  },
];

export const ERD_RELATIONS: ErdRelation[] = [
  { from: 'Org', to: 'Project', fromCol: 'id', toCol: 'orgId', cardinality: '1:N' },
  { from: 'Org', to: 'Membership', fromCol: 'id', toCol: 'orgId', cardinality: '1:N' },
  { from: 'Org', to: 'OrgSetting', fromCol: 'id', toCol: 'orgId', cardinality: '1:N' },
  { from: 'User', to: 'Membership', fromCol: 'id', toCol: 'userId', cardinality: '1:N' },
  { from: 'User', to: 'Issue', fromCol: 'id', toCol: 'createdByUserId', cardinality: '1:N' },
  { from: 'Project', to: 'Issue', fromCol: 'id', toCol: 'projectId', cardinality: '1:N' },
  { from: 'Project', to: 'Run', fromCol: 'id', toCol: 'projectId', cardinality: '1:N' },
  { from: 'Project', to: 'Loop', fromCol: 'id', toCol: 'projectId', cardinality: '1:N' },
  { from: 'Project', to: 'PrototypeDeployment', fromCol: 'id', toCol: 'projectId', cardinality: '1:1' },
  { from: 'Project', to: 'ProjectMemory', fromCol: 'id', toCol: 'projectId', cardinality: '1:1' },
  { from: 'Project', to: 'ProjectWiki', fromCol: 'id', toCol: 'projectId', cardinality: '1:1' },
  { from: 'Loop', to: 'Issue', fromCol: 'id', toCol: 'loopId', cardinality: '1:N' },
  { from: 'Issue', to: 'Run', fromCol: 'id', toCol: 'issueId', cardinality: '1:N' },
  { from: 'Issue', to: 'IssueEvent', fromCol: 'id', toCol: 'issueId', cardinality: '1:N' },
  { from: 'Run', to: 'Stage', fromCol: 'id', toCol: 'runId', cardinality: '1:N' },
  { from: 'Run', to: 'LogLine', fromCol: 'id', toCol: 'runId', cardinality: '1:N' },
  { from: 'Run', to: 'StageAttempt', fromCol: 'id', toCol: 'runId', cardinality: '1:N' },
  { from: 'PrototypeDeployment', to: 'PrototypeSecret', fromCol: 'id', toCol: 'deploymentId', cardinality: '1:N' },
  { from: 'PrototypeDeployment', to: 'PrototypeCustomDomain', fromCol: 'id', toCol: 'deploymentId', cardinality: '1:N' },
];

export const ERD_GROUP_LABELS: Record<ErdGroup, string> = {
  core: 'Core pipeline',
  loop: 'Scheduled loops',
  prototype: 'Prototype deploys',
  config: 'Config & auth',
  analytics: 'Analytics',
};
